import { TFunction } from 'i18next';

import {
  Form,
  WorkflowArea,
  AdminJourneyStage,
  PatientJourneyStage,
  ProviderJourneyStage,
} from 'types';

interface StepInternalColor {
  backgroundDark: string;
  backgroundLight: string;
}

interface StepData extends StepInternalColor {
  iconPath: string;
  showDivider: boolean;
  backgroundLight70: string;
  backgroundLight90: string;
}

export type PatientStageColors = Partial<Record<PatientJourneyStage, string>>;
export type ProviderStageColors = Partial<Record<ProviderJourneyStage, string>>;

const providerIconPaths: Record<ProviderJourneyStage, string> = {
  [ProviderJourneyStage.PRE_COURSE]: '/icons/1.svg',
  [ProviderJourneyStage.POST_COURSE]: '/icons/8.svg',
  [ProviderJourneyStage.INTRODUCTION]: '/icons/2.svg',
  [ProviderJourneyStage.PREOPERATIVE]: '/icons/4.svg',
  [ProviderJourneyStage.ERP_WORKFLOW]: '/icons/7.svg',
  [ProviderJourneyStage.PREADMISSION]: '/icons/3.svg',
  [ProviderJourneyStage.POSTOPERATIVE]: '/icons/6.svg',
  [ProviderJourneyStage.INTRAOPERATIVE]: '/icons/5.svg',
};

const patientIconPaths: Record<PatientJourneyStage, string> = {
  [PatientJourneyStage.PRE_COURSE]: '/icons/1.svg',
  [PatientJourneyStage.POST_COURSE]: '/icons/8.svg',
  [PatientJourneyStage.INTRODUCTION]: '/icons/2.svg',
  [PatientJourneyStage.AFTER_SURGERY]: '/icons/5.svg',
  [PatientJourneyStage.BEFORE_SURGERY]: '/icons/3.svg',
  [PatientJourneyStage.DAY_OF_SURGERY]: '/icons/4.svg',
  [PatientJourneyStage.DAYS_AFTER_SURGERY]: '/icons/6.svg',
  [PatientJourneyStage.ENHANCED_RECOVERY_JOURNEY]: '/icons/7.svg',
};

const adminIconPaths: Record<AdminJourneyStage, string> = {
  [AdminJourneyStage.PLAN]: '/icons/7.svg',
  [AdminJourneyStage.CASE_STUDY]: '/icons/3.svg',
  [AdminJourneyStage.INTRODUCTION]: '/icons/1.svg',
  [AdminJourneyStage.OVERCOMING_BARRIERS]: '/icons/9.svg',
};

const dividers = new Set([
  PatientJourneyStage.INTRODUCTION,
  PatientJourneyStage.DAYS_AFTER_SURGERY,
  ProviderJourneyStage.INTRODUCTION,
  ProviderJourneyStage.POSTOPERATIVE,
]);

const stepColors: Record<string, StepInternalColor> = {
  '#FBB5B3': {
    backgroundDark: 'linear-gradient(161deg, #EF504C 3.6%, #DD2723 86.81%)',
    backgroundLight: 'linear-gradient(161deg, #FBB5B3 3.6%, #E88280 86.81%)',
  },
  '#F6C7A0': {
    backgroundDark: 'linear-gradient(159deg, #D47A31 2.55%, #DA6303 95.38%)',
    backgroundLight: 'linear-gradient(159deg, #F6C7A0 2.55%, #DD9F6D 95.38%)',
  },
  '#E6B4E0': {
    backgroundDark: 'linear-gradient(152deg, #D847C6 4.15%, #B521A4 99.24%)',
    backgroundLight: 'linear-gradient(152deg, #E6B4E0 4.15%, #CC8BC4 99.24%)',
  },
  '#8EC4FE': {
    backgroundDark: 'linear-gradient(124deg, #3185DF 19.11%, #1B73D0 94.61%)',
    backgroundLight: 'linear-gradient(124deg, #8EC4FE 19.11%, #569CE7 94.61%)',
  },
  '#56DD8E': {
    backgroundDark: 'linear-gradient(123deg, #15A763 19.79%, #068244 87.83%)',
    backgroundLight: 'linear-gradient(123deg, #56DD9E 19.79%, #2BC377 87.83%)',
  },
  '#A4C1E0': {
    backgroundDark: 'linear-gradient(133deg, #607B97 15.14%, #395471 104.43%)',
    backgroundLight: 'linear-gradient(133deg, #A4C1E0 15.14%, #7392B2 104.43%)',
  },
};

const areaColorMap: {
  patient: PatientStageColors;
  provider: ProviderStageColors;
} = {
  provider: {
    [ProviderJourneyStage.PREADMISSION]: '#2BC36A',
    [ProviderJourneyStage.PREOPERATIVE]: '#DF965B',
    [ProviderJourneyStage.POSTOPERATIVE]: '#DF605D',
    [ProviderJourneyStage.INTRAOPERATIVE]: '#AB68A3',
  },
  patient: {
    [PatientJourneyStage.AFTER_SURGERY]: '#AB68A3',
    [PatientJourneyStage.BEFORE_SURGERY]: '#2BC36A',
    [PatientJourneyStage.DAY_OF_SURGERY]: '#DF965B',
    [PatientJourneyStage.DAYS_AFTER_SURGERY]: '#DF605D',
  },
};

const providerStagesSet = new Set<ProviderJourneyStage>(
  Object.values(ProviderJourneyStage)
);

export const isProviderType = (codeName: string) =>
  providerStagesSet.has(codeName as ProviderJourneyStage);

export const isERPStep = (codeName: string) =>
  codeName === ProviderJourneyStage.ERP_WORKFLOW ||
  codeName === PatientJourneyStage.ENHANCED_RECOVERY_JOURNEY;

export const isOrgPlanStep = (codeName: string) =>
  codeName === AdminJourneyStage.PLAN;

export const getStepFormFlags = (
  formVersions: Form[] | undefined,
  codeName: string
) => {
  const hasForm = formVersions && formVersions.length > 0;
  return {
    isERP: hasForm && isERPStep(codeName),
    isOrgPlan: hasForm && isOrgPlanStep(codeName),
  };
};

const iconPaths: Record<string, string> = {
  ...providerIconPaths,
  ...patientIconPaths,
  ...adminIconPaths,
};

export const getStepData = (color: string, codeName: string): StepData => {
  const isProvider = isProviderType(codeName);

  const stepColor = stepColors[color] || {
    shadowColor: 'rgba(0, 0, 0, 0.12)',
    backgroundDark: `linear-gradient(135deg, ${color} 0%, ${color} 100%)`,
    backgroundLight: `linear-gradient(135deg, ${color} 0%, ${color} 100%)`,
  };
  const backgroundLight70 = `linear-gradient(0deg, rgba(255, 255, 255, 0.70) 0%, rgba(255, 255, 255, 0.70) 100%), ${stepColor.backgroundLight}`;
  const backgroundLight90 = `linear-gradient(0deg, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.90) 100%), ${stepColor.backgroundLight}`;

  const iconPath = iconPaths[codeName];

  return {
    ...stepColor,
    backgroundLight70,
    backgroundLight90,
    iconPath: iconPath || '/icons/1.svg',
    showDivider: dividers.has(
      isProvider
        ? (codeName as ProviderJourneyStage)
        : (codeName as PatientJourneyStage)
    ),
  };
};

export const getAreaColor = (label: string, isProvider?: boolean) => {
  const color = isProvider
    ? areaColorMap.provider[label as ProviderJourneyStage]
    : areaColorMap.patient[label as PatientJourneyStage];
  return color ?? 'black';
};

export const erpImageMap: Record<string, string> = {
  Walk: '/images/erp/Walk.png',
  'Early Walking': '/images/erp/Walk.png',
  'Eat Food': '/images/erp/Early-Diet.png',
  'Bowel Prep': '/images/erp/Bowel-Prep.png',
  'Early Diet': '/images/erp/Early-Diet.png',
  'Pain Block': '/images/erp/Pain-Block.png',
  'Get the Block': '/images/erp/Pain-Block.png',
  'Oral Antibiotics': '/images/erp/Oral-Antibiotics.png',
  'No NG Tube Placed': '/images/erp/No-NG-Tube-Placed.png',
  'VTE Prophylaxis': '/images/erp/VTE-Chemoprophylaxis.png',
  'Drink Clear Liquids': '/images/erp/Drink-Clear-Liquids.png',
  'Multimodal Analgesia': '/images/erp/Take-Pain-Medications.png',
  'Take Approved Medications': '/images/erp/Oral-Antibiotics.png',
  'Discontinue IV Fluids': '/images/erp/Discontinue-IV-Fluids.png',
  'Maintain Normothermia': '/images/erp/Maintain-Normothermia.png',
  'Take Pain Medications': '/images/erp/Take-Pain-Medications.png',
  'Anti-Nausea Medication': '/images/erp/Anti-Nausea-Medication.png',
  'Multimodal Medications': '/images/erp/Multimodal-Medications.png',
  'Preoperation Education': '/images/erp/Preoperation-Education.png',
  'Remove Foley Catheters': '/images/erp/Remove-Foley-Catheters.png',
  'Learn about your Surgery': '/images/erp/Preoperation-Education.png',
  'IV Fluids Will Be Stopped': '/images/erp/Discontinue-IV-Fluids.png',
  'Minimally Invasive Surgery': '/images/erp/Minimally-Invasive-Surgery.png',
  'Drink Clear Liquids as Instructed': '/images/erp/Drink-Clear-Liquids.png',
  'Goal-Directed Fluid Therapy': '/images/erp/Goal-Directed-Fluid-Therapy.png',
  'Catheters/Drains Will Be Removed': '/images/erp/Remove-Foley-Catheters.png',
  'Get Blood Clot Prevention Medication':
    '/images/erp/VTE-Chemoprophylaxis.png',
  'Review information from Healthcare Team':
    '/images/erp/Review-Information-from-Healthcare-Team.png',
};

const providerCodeNameToTranslationKey: Partial<
  Record<ProviderJourneyStage, string>
> = {
  [ProviderJourneyStage.PREADMISSION]: 'preadmissionTitle',
  [ProviderJourneyStage.PREOPERATIVE]: 'preoperativeTitle',
  [ProviderJourneyStage.POSTOPERATIVE]: 'postoperativeTitle',
  [ProviderJourneyStage.INTRAOPERATIVE]: 'intraoperativeTitle',
};

const patientCodeNameToTranslationKey: Partial<
  Record<PatientJourneyStage, string>
> = {
  [PatientJourneyStage.AFTER_SURGERY]: 'afterSurgeryTitle',
  [PatientJourneyStage.DAY_OF_SURGERY]: 'dayOfSurgeryTitle',
  [PatientJourneyStage.BEFORE_SURGERY]: 'beforeSurgeryTitle',
  [PatientJourneyStage.DAYS_AFTER_SURGERY]: 'daysAfterSurgeryTitle',
};

const getProviderWorkflowData = (t: TFunction): WorkflowArea[] => [
  {
    id: '1',
    codeName: ProviderJourneyStage.PREADMISSION,
    color: getAreaColor(ProviderJourneyStage.PREADMISSION, true),
    title: t(
      providerCodeNameToTranslationKey[ProviderJourneyStage.PREADMISSION]!
    ),
    children: [
      {
        id: '1',
        title: t('preoperationEducation'),
        image: erpImageMap['Preoperation Education'],
      },
      {
        id: '2',
        title: t('drinkClearLiquids'),
        image: erpImageMap['Drink Clear Liquids'],
      },
      {
        id: '3',
        title: t('bowelPrep'),
        image: erpImageMap['Bowel Prep'],
      },
      {
        id: '4',
        title: t('oralAntibiotics'),
        image: erpImageMap['Oral Antibiotics'],
      },
    ],
  },
  {
    id: '5',
    codeName: ProviderJourneyStage.PREOPERATIVE,
    color: getAreaColor(ProviderJourneyStage.PREOPERATIVE, true),
    title: t(
      providerCodeNameToTranslationKey[ProviderJourneyStage.PREOPERATIVE]!
    ),
    children: [
      {
        id: '6',
        title: t('painBlock'),
        image: erpImageMap['Pain Block'],
      },
      {
        id: '7',
        title: t('vteProphylaxis'),
        image: erpImageMap['VTE Prophylaxis'],
      },
      {
        id: '8',
        title: t('multimodalAnalgesia'),
        image: erpImageMap['Multimodal Analgesia'],
      },
    ],
  },
  {
    id: '9',
    codeName: ProviderJourneyStage.INTRAOPERATIVE,
    color: getAreaColor(ProviderJourneyStage.INTRAOPERATIVE, true),
    title: t(
      providerCodeNameToTranslationKey[ProviderJourneyStage.INTRAOPERATIVE]!
    ),
    children: [
      {
        id: '10',
        title: t('minimallyInvasiveSurgery'),
        image: erpImageMap['Minimally Invasive Surgery'],
      },
      {
        id: '11',
        title: t('maintainNormothermia'),
        image: erpImageMap['Maintain Normothermia'],
      },
      {
        id: '12',
        title: t('goalDirectedFluidTherapy'),
        image: erpImageMap['Goal-Directed Fluid Therapy'],
      },
      {
        id: '13',
        title: t('antiNauseaMedication'),
        image: erpImageMap['Anti-Nausea Medication'],
      },
    ],
  },
  {
    id: '14',
    codeName: ProviderJourneyStage.POSTOPERATIVE,
    color: getAreaColor(ProviderJourneyStage.POSTOPERATIVE, true),
    title: t(
      providerCodeNameToTranslationKey[ProviderJourneyStage.POSTOPERATIVE]!
    ),
    children: [
      {
        id: '15',
        title: t('earlyDiet'),
        image: erpImageMap['Early Diet'],
      },
      {
        id: '16',
        title: t('multimodalMedications'),
        image: erpImageMap['Multimodal Medications'],
      },
      {
        id: '17',
        title: t('earlyWalking'),
        image: erpImageMap['Early Walking'],
      },
      {
        id: '18',
        title: t('discontinueIVFluids'),
        image: erpImageMap['Discontinue IV Fluids'],
      },
      {
        id: '19',
        title: t('removeFoleyCatheters'),
        image: erpImageMap['Remove Foley Catheters'],
      },
      {
        id: '20',
        title: t('vteProphylaxis'),
        image: erpImageMap['VTE Prophylaxis'],
      },
      {
        id: '21',
        title: t('noNGTubePlaced'),
        image: erpImageMap['No NG Tube Placed'],
      },
    ],
  },
];

const getPatientWorkflowData = (t: TFunction): WorkflowArea[] => [
  {
    id: '1',
    codeName: PatientJourneyStage.BEFORE_SURGERY,
    color: getAreaColor(PatientJourneyStage.BEFORE_SURGERY),
    title: t(
      patientCodeNameToTranslationKey[PatientJourneyStage.BEFORE_SURGERY]!
    ),
    children: [
      {
        id: '1',
        title: t('learnAboutYourSurgery'),
        image: erpImageMap['Learn about your Surgery'],
      },
      {
        id: '2',
        title: t('takeApprovedMedications'),
        image: erpImageMap['Take Approved Medications'],
      },
      {
        id: '3',
        title: t('drinkClearLiquidsAsInstructed'),
        image: erpImageMap['Drink Clear Liquids as Instructed'],
      },
      {
        id: '4',
        title: t('reviewInformationFromHealthcareTeam'),
        image: erpImageMap['Review information from Healthcare Team'],
      },
    ],
  },
  {
    id: '5',
    codeName: PatientJourneyStage.DAY_OF_SURGERY,
    color: getAreaColor(PatientJourneyStage.DAY_OF_SURGERY),
    title: t(
      patientCodeNameToTranslationKey[PatientJourneyStage.DAY_OF_SURGERY]!
    ),
    children: [
      {
        id: '6',
        title: t('getTheBlock'),
        image: erpImageMap['Get the Block'],
      },
      {
        id: '7',
        title: t('getBloodClotPreventionMedication'),
        image: erpImageMap['Get Blood Clot Prevention Medication'],
      },
      {
        id: '8',
        title: t('takePainMedications'),
        image: erpImageMap['Take Pain Medications'],
      },
    ],
  },
  {
    id: '9',
    codeName: PatientJourneyStage.AFTER_SURGERY,
    color: getAreaColor(PatientJourneyStage.AFTER_SURGERY),
    title: t(
      patientCodeNameToTranslationKey[PatientJourneyStage.AFTER_SURGERY]!
    ),
    children: [
      {
        id: '10',
        title: t('walk'),
        image: erpImageMap['Walk'],
      },
      {
        id: '11',
        title: t('eatFood'),
        image: erpImageMap['Eat Food'],
      },
      {
        id: '12',
        title: t('takePainMedications'),
        image: erpImageMap['Take Pain Medications'],
      },
      {
        id: '13',
        title: t('getBloodClotPreventionMedication'),
        image: erpImageMap['Get Blood Clot Prevention Medication'],
      },
    ],
  },
  {
    id: '14',
    codeName: PatientJourneyStage.DAYS_AFTER_SURGERY,
    color: getAreaColor(PatientJourneyStage.DAYS_AFTER_SURGERY),
    title: t(
      patientCodeNameToTranslationKey[PatientJourneyStage.DAYS_AFTER_SURGERY]!
    ),
    children: [
      {
        id: '15',
        title: t('walk'),
        image: erpImageMap['Walk'],
      },
      {
        id: '16',
        title: t('cathetersDrainsWillBeRemoved'),
        image: erpImageMap['Catheters/Drains Will Be Removed'],
      },
      {
        id: '17',
        title: t('eatFood'),
        image: erpImageMap['Eat Food'],
      },
      {
        id: '18',
        title: t('takePainMedications'),
        image: erpImageMap['Take Pain Medications'],
      },
      {
        id: '19',
        title: t('IVFluidsWillBeStopped'),
        image: erpImageMap['IV Fluids Will Be Stopped'],
      },
    ],
  },
];

export const getWorkflowData = (
  isProvider: boolean,
  t: TFunction
): WorkflowArea[] => {
  return isProvider ? getProviderWorkflowData(t) : getPatientWorkflowData(t);
};
